import { Injectable, signal, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { User } from '../models/user.model';
import { BaseApiService } from './base-api.service';
import { API_CONSTANTS } from '../constants/api.constants';
import { UserService } from './user.service';
import { LoginRequest, LoginResponse } from '../models/auth.model';
import { StorageService } from './storage.service';

@Injectable({
    providedIn: 'root'
})
export class AuthService extends BaseApiService {
    private router = inject(Router);
    private userService = inject(UserService);
    private storageService = inject(StorageService);

    // Auth state using signals
    private _user = signal<User | null>(null);
    public user = this._user.asReadonly();
    public isAuthenticated = computed(() => !!this._user());

    // Memory-only user ID state
    private currentUserId: string | null = null;

    // Flag and subject for refresh token queueing
    public isRefreshingToken = false;
    public refreshTokenSubject = new BehaviorSubject<boolean>(false);

    /**
     * Performs login request and loads current user profile on success.
     */
    login(email: string, password: string): Observable<User> {
        const payload: LoginRequest = {
            grant_type: 'password',
            email,
            password
        };

        return this.post<any>(API_CONSTANTS.ENDPOINTS.AUTH.LOGIN, payload).pipe(
            switchMap(response => {
                const userId = this.getUserIdFromResponse(response);
                const isSuccess = response?.success || !!response?.user || !!response?.data?.user;

                if (isSuccess && userId) {
                    const userRole = response?.data?.user?.role || response?.user?.role || response?.role;
                    const loginOrgName = response?.data?.user?.organization_name || response?.user?.organization_name || response?.organization_name || '';
                    const organizationId = response?.data?.user?.organization_id || response?.user?.organization_id || '';
                    const organizationUnitId = response?.data?.user?.organization_unit_id || response?.user?.organization_unit_id || '';

                    this.currentUserId = userId;
                    this.storageService.set('auth_user_id', userId);
                    if (loginOrgName) {
                        this.storageService.set('auth_user_org', loginOrgName);
                    }
                    if (organizationId) {
                        this.storageService.set('auth_user_org_id', organizationId.toString());
                    }
                    if (organizationUnitId) {
                        this.storageService.set('auth_user_org_unit_id', organizationUnitId.toString());
                    }
                    if (userRole) {
                        this.storageService.set('auth_user_role', userRole);
                    }

                    return this.loadCurrentUser(userId).pipe(
                        tap(user => {
                            if (userRole) {
                                user.role = userRole;
                            }
                            if (loginOrgName && !user.organizationName) {
                                user.organizationName = loginOrgName;
                            }
                            if (organizationId) {
                                user.organization_id = organizationId;
                            }
                            if (organizationUnitId) {
                                user.organization_unit_id = organizationUnitId;
                            }
                            this._user.set({
                                ...user,
                                role: userRole || user.role,
                                organizationName: user.organizationName || loginOrgName,
                                organization_id: organizationId || user.organization_id,
                                organization_unit_id: organizationUnitId || user.organization_unit_id
                            });
                        })
                    );
                }
                return throwError(() => new Error(response?.message || 'Login failed'));
            })
        );
    }

    /**
     * Restores authentication session during app initialization.
     */
    initAuth(): Observable<User | null> {
        const userId = this.storageService.get<string>('auth_user_id');
        if (userId) {
            this.currentUserId = userId;
            return this.loadCurrentUser(userId).pipe(
                catchError(err => {
                    console.error('Failed to restore authentication session:', err);
                    this.storageService.remove('auth_user_id');
                    this.currentUserId = null;
                    this._user.set(null);
                    this.userService.clearState();
                    return of(null);
                })
            );
        }
        return of(null);
    }

    /**
     * Fetches current user profile using the existing Get User By ID API.
     */
    loadCurrentUser(userId: string): Observable<User> {
        return this.userService.getUserById(userId).pipe(
            tap(domainUser => {
                const storedOrg = this.storageService.get<string>('auth_user_org');
                if (storedOrg && !domainUser.organizationName) {
                    domainUser.organizationName = storedOrg;
                }
                const storedOrgId = this.storageService.get<string>('auth_user_org_id');
                if (storedOrgId) {
                    domainUser.organization_id = storedOrgId;
                }
                const storedOrgUnitId = this.storageService.get<string>('auth_user_org_unit_id');
                if (storedOrgUnitId) {
                    domainUser.organization_unit_id = storedOrgUnitId;
                }
                const storedRole = this.storageService.get<string>('auth_user_role');
                if (storedRole) {
                    domainUser.role = storedRole;
                }
                this._user.set(domainUser);
            }),
            catchError(err => {
                this._user.set(null);
                return throwError(() => err);
            })
        );
    }

    /**
     * Triggers token refresh by hitting POST /users/login with refresh_token grant type.
     * The browser automatically handles HttpOnly cookie transmission via withCredentials.
     */
    refreshToken(): Observable<any> {
        const payload = {
            grant_type: 'refresh_token',
            refresh_token: ''
        };
        return this.post<any>(API_CONSTANTS.ENDPOINTS.AUTH.LOGIN, payload);
    }

    forgotPassword(email: string): Observable<any> {
        return this.post<any>(API_CONSTANTS.ENDPOINTS.AUTH.FORGOT_PASSWORD, { email });
    }

    resetPassword(password: string, token: string): Observable<any> {
        return this.post<any>(API_CONSTANTS.ENDPOINTS.AUTH.RESET_PASSWORD, { password, token });
    }

    logout(): void {
        this.post<any>(API_CONSTANTS.ENDPOINTS.AUTH.LOGOUT).pipe(
            catchError((err) => {
                console.error('Logout API failed:', err);
                return of(null);
            })
        ).subscribe(() => {
            this._user.set(null);
            this.currentUserId = null;
            this.storageService.remove('auth_user_id');
            this.storageService.remove('auth_user_org');
            this.storageService.remove('auth_user_org_id');
            this.storageService.remove('auth_user_org_unit_id');
            this.storageService.remove('auth_user_role');
            this.userService.clearState();
            this.router.navigate(['/login']);
        });
    }

    /**
     * Extracts user ID from the login response.
     */
    private getUserIdFromResponse(response: any): string | null {
        if (response?.data?.user?.user_id) {
            return response.data.user.user_id.toString();
        }
        if (response?.user?.user_id) {
            return response.user.user_id.toString();
        }
        if (response?.data?.user_id) {
            return response.data.user_id.toString();
        }
        if (response?.user_id) {
            return response.user_id.toString();
        }
        return null;
    }
}
